import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

import logoDsMin from '../assets/logos/logo-ds-min.png?url';

@customElement('loading-screen')
export class LoadingScreen extends LitElement {
  // ✅ NUEVO: Estado interno para detectar tema automáticamente
  @state() private currentTheme: 'light' | 'dark' = 'light';

  // ✅ OPCIONAL: Propiedad para texto de carga personalizado
  @property({ type: String }) message?: string;

  // ✅ OPCIONAL: Propiedad para tamaño del spinner
  @property({ type: String }) size: 'small' | 'medium' | 'large' = 'medium';

  static styles = css`
    :host {
      /* ✅ CENTRADO COMPLETO: Ocupa toda la pantalla disponible */
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      z-index: 9999;
      
      /* ✅ CENTRADO FLEXBOX: Centrado perfecto horizontal y vertical */
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      
      /* ✅ TEMA AUTOMÁTICO: Variables que se adaptan al tema global */
      background-color: var(--loading-bg, rgba(255, 255, 255, 0.95));
      color: var(--loading-text, #1f2937);
      
      /* ✅ BACKDROP: Efecto de desenfoque */
      backdrop-filter: blur(8px);
      
      /* ✅ TRANSICIONES: Suavizado de cambios */
      transition: all 0.3s ease;
    }

    /* ✅ ADAPTACIÓN AUTOMÁTICA AL TEMA OSCURO */
    :host([data-theme="dark"]),
    :host-context([data-theme="dark"]) {
      background-color: rgba(30, 34, 41, 0.95);
      color: #f1f5f9;
      
      --spinner-bg: #3b82f6;
      --spinner-fg: #f59e0b;
      --logo-filter: brightness(1.2) contrast(1.1);
    }

    /* ✅ VARIABLES CSS PARA TEMAS */
    :host {
      /* Tema claro (por defecto) */
      --spinner-bg: #0075c9;
      --spinner-fg: #ff8800;
      --logo-filter: none;
    }

    /* ✅ CONTENEDOR DEL SPINNER CON TAMAÑOS DINÁMICOS */
    .loading-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1.5rem;
    }

    .spinner {
      position: relative;
      /* ✅ TAMAÑOS DINÁMICOS */
      width: var(--spinner-size, 120px);
      height: var(--spinner-size, 120px);
    }

    /* ✅ TAMAÑOS RESPONSIVOS */
    :host([size="small"]) {
      --spinner-size: 80px;
      --logo-size: 32px;
    }

    :host([size="medium"]) {
      --spinner-size: 120px;
      --logo-size: 48px;
    }

    :host([size="large"]) {
      --spinner-size: 160px;
      --logo-size: 64px;
    }

    .spinner circle.bg {
      fill: none;
      stroke: var(--spinner-bg);
      stroke-width: 8;
      opacity: 0.3;
    }

    .spinner circle.fg {
      fill: none;
      stroke: var(--spinner-fg);
      stroke-width: 8;
      stroke-dasharray: 80 200;
      stroke-dashoffset: 0;
      transform-origin: 50% 50%;
      animation: rotate 1.5s linear infinite;
    }

    .logo {
      position: absolute;
      top: 50%;
      left: 50%;
      width: var(--logo-size, 48px);
      height: var(--logo-size, 48px);
      transform: translate(-50%, -50%) scale(1);
      animation: pulse 2s ease-in-out infinite;
    }

    .logo img {
      width: 100%;
      height: auto;
      margin-top: 10px;
      filter: var(--logo-filter);
      transition: filter 0.3s ease;
    }

    /* ✅ MENSAJE DE CARGA */
    .loading-message {
      font-size: 1rem;
      font-weight: 500;
      color: inherit;
      text-align: center;
      opacity: 0.8;
      letter-spacing: 0.025em;
    }

    /* ✅ ANIMACIONES */
    @keyframes rotate {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    @keyframes pulse {
      0%, 100% { 
        transform: translate(-50%, -50%) scale(1);
        opacity: 1;
      }
      50% { 
        transform: translate(-50%, -50%) scale(1.05);
        opacity: 0.8;
      }
    }

    @keyframes fadeIn {
      from { 
        opacity: 0;
        transform: scale(0.9);
      }
      to { 
        opacity: 1;
        transform: scale(1);
      }
    }

    /* ✅ ANIMACIÓN DE ENTRADA */
    .loading-container {
      animation: fadeIn 0.5s ease-out;
    }

    /* ✅ RESPONSIVE PARA MÓVILES */
    @media (max-width: 768px) {
      :host {
        gap: 1rem;
      }

      :host([size="small"]) {
        --spinner-size: 60px;
        --logo-size: 24px;
      }

      :host([size="medium"]) {
        --spinner-size: 80px;
        --logo-size: 32px;
      }

      :host([size="large"]) {
        --spinner-size: 100px;
        --logo-size: 40px;
      }

      .loading-message {
        font-size: 0.875rem;
        padding: 0 1rem;
      }
    }

    @media (max-width: 480px) {
      :host {
        gap: 0.75rem;
      }

      :host([size="small"]) {
        --spinner-size: 50px;
        --logo-size: 20px;
      }

      :host([size="medium"]) {
        --spinner-size: 70px;
        --logo-size: 28px;
      }

      :host([size="large"]) {
        --spinner-size: 90px;
        --logo-size: 36px;
      }

      .loading-message {
        font-size: 0.8rem;
      }
    }

    /* ✅ ACCESIBILIDAD: Reducir animaciones si se prefiere */
    @media (prefers-reduced-motion: reduce) {
      .spinner circle.fg {
        animation: none;
        stroke-dasharray: none;
        stroke-dashoffset: 0;
      }
      
      .logo {
        animation: none;
        transform: translate(-50%, -50%) scale(1);
      }

      .loading-container {
        animation: none;
      }
    }

    /* ✅ SOPORTE PARA MODO DE ALTO CONTRASTE */
    @media (prefers-contrast: high) {
      :host {
        background-color: var(--loading-bg, rgba(255, 255, 255, 0.98));
      }

      .spinner circle.bg {
        opacity: 0.6;
      }

      .loading-message {
        opacity: 1;
        font-weight: 600;
      }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    // ✅ DETECTAR tema inicial
    this.detectTheme();
    
    // ✅ OBSERVAR cambios de tema
    this.observeThemeChanges();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // ✅ LIMPIAR observador
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  private themeObserver?: MutationObserver;

  private detectTheme() {
    // ✅ DETECTAR tema desde el documento
    const documentTheme = document.documentElement.getAttribute('data-theme');
    const bodyTheme = document.body.getAttribute('data-theme');
    
    this.currentTheme = documentTheme === 'dark' || bodyTheme === 'dark' ? 'dark' : 'light';
    
    // ✅ APLICAR tema al host
    this.setAttribute('data-theme', this.currentTheme);
  }

  private observeThemeChanges() {
    // ✅ OBSERVAR cambios en el atributo data-theme del documento
    this.themeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'data-theme' || mutation.attributeName === 'class')) {
          this.detectTheme();
        }
      });
    });

    // ✅ OBSERVAR tanto en html como en body
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class']
    });

    this.themeObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-theme', 'class']
    });
  }

  render() {
    return html`
      <div class="loading-container">
        <div class="spinner">
          <svg viewBox="0 0 100 100">
            <circle class="bg" cx="50" cy="50" r="45"></circle>
            <circle class="fg" cx="50" cy="50" r="45"></circle>
          </svg>
          <div class="logo">
            <img src="${logoDsMin}" alt="Logo Droguería Suiza">
          </div>
        </div>
        ${this.message ? html`
          <div class="loading-message">${this.message}</div>
        ` : ''}
      </div>
    `;
  }
}